import * as XLSX from "xlsx";

/* ── helpers ── */
const fmt = (n) => Math.round(n || 0);
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

/**
 * Apply column widths to a worksheet.
 */
function setColWidths(ws, widths) {
  ws["!cols"] = widths.map((w) => ({ wch: w }));
}

/**
 * Build the SUMMARY sheet (one row per chapter).
 */
function buildSummarySheet(allStats) {
  const headers = [
    "Chapter Name",
    "District",
    "Target Students",
    "Target Amount (₹)",
    "Acquired Students",
    "Acquired Amount (₹)",
    "Paid Students",
    "Collected Amount (₹)",
    "Completion %",
    "Total Sponsorships",
    "Completed Sponsorships",
    "Sponsorship Conversion %",
    "Total Payments",
  ];

  const rows = allStats.map((s) => [
    s.chapter.name,
    s.chapter.district || "—",
    s.target.studentCount,
    fmt(s.target.amountINR),
    s.acquired.studentCount,
    fmt(s.acquired.amountINR),
    s.collected.studentCount,
    fmt(s.collected.amountINR),
    pct(s.collected.amountINR, s.target.amountINR),
    s.conversion.countTotal,
    s.conversion.countCompleted,
    s.conversion.amountRate,
    s.activity.totalPayments,
  ]);

  const data = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(data);

  setColWidths(ws, [28, 18, 16, 18, 18, 20, 16, 20, 14, 18, 22, 22, 14]);
  return ws;
}

/**
 * Build a detailed sheet for a single chapter.
 */
function buildChapterSheet(s) {
  const R = []; // rows accumulator

  const section = (title) => {
    R.push([]);
    R.push([title]);
  };

  const kv = (key, value) => R.push([key, value]);

  // ── Heading
  R.push([`Chapter: ${s.chapter.name}`]);
  if (s.chapter.district) kv("District", s.chapter.district);
  R.push([]);

  // ── Target Configuration
  section("TARGET CONFIGURATION");
  kv("Target Students", s.target.studentCount);
  kv("Target Amount (₹)", fmt(s.target.amountINR));
  kv("Cost per Student – INR", s.target.costPerStudentINR);
  kv("Cost per Student – Foreign", s.target.costPerStudentForeign);

  // ── Acquired
  section("ACQUIRED");
  kv("Total Acquired Students", s.acquired.studentCount);
  kv("Total Acquired Amount (₹)", fmt(s.acquired.amountINR));
  kv(
    "  → From Sponsorships – Students",
    s.acquired.fromSponsorships.studentCount,
  );
  kv(
    "  → From Sponsorships – Amount (₹)",
    fmt(s.acquired.fromSponsorships.amountINR),
  );
  kv(
    "  → From Standalone Payments – Students",
    s.acquired.fromStandalone.studentCount,
  );
  kv(
    "  → From Standalone Payments – Amount (₹)",
    fmt(s.acquired.fromStandalone.amountINR),
  );

  // ── Collected
  section("COLLECTED (PAID)");
  kv("Total Paid Students", s.collected.studentCount);
  kv("Total Collected Amount (₹)", fmt(s.collected.amountINR));
  kv(
    "  → From Sponsorships – Students",
    s.collected.fromSponsorships.studentCount,
  );
  kv(
    "  → From Sponsorships – Amount (₹)",
    fmt(s.collected.fromSponsorships.amountINR),
  );
  kv(
    "  → From Standalone Payments – Students",
    s.collected.fromStandalone.studentCount,
  );
  kv(
    "  → From Standalone Payments – Amount (₹)",
    fmt(s.collected.fromStandalone.amountINR),
  );

  // ── Progress vs Target
  section("PROGRESS VS TARGET");
  kv("Acquired vs Target (%)", pct(s.acquired.amountINR, s.target.amountINR));
  kv("Collected vs Target (%)", pct(s.collected.amountINR, s.target.amountINR));

  // ── Sponsorship Conversion
  section("SPONSORSHIP CONVERSION");
  kv("Amount Conversion Rate (%)", s.conversion.amountRate);
  kv("Count Conversion Rate (%)", s.conversion.countRate);
  kv("Total Sponsorships", s.conversion.countTotal);
  kv("Completed", s.conversion.countCompleted);
  kv("Active", s.conversion.countActive);
  kv("Pending", s.conversion.countPending);

  // ── Sponsorship Categories table
  section("SPONSORSHIP CATEGORIES");
  R.push([
    "Category",
    "Count",
    "Promised (₹)",
    "Collected (₹)",
    "Collection %",
  ]);
  R.push([
    "Mahallu",
    s.categories.mahallu.count,
    fmt(s.categories.mahallu.promisedINR),
    fmt(s.categories.mahallu.paidINR),
    pct(s.categories.mahallu.paidINR, s.categories.mahallu.promisedINR),
  ]);
  R.push([
    "Individual",
    s.categories.individual.count,
    fmt(s.categories.individual.promisedINR),
    fmt(s.categories.individual.paidINR),
    pct(s.categories.individual.paidINR, s.categories.individual.promisedINR),
  ]);
  R.push([
    "TOTAL",
    s.categories.aggregate.count,
    fmt(s.categories.aggregate.promisedINR),
    fmt(s.categories.aggregate.paidINR),
    pct(s.categories.aggregate.paidINR, s.categories.aggregate.promisedINR),
  ]);

  // ── Mahallu Breakdown
  if (s.mahalluBreakdown && s.mahalluBreakdown.length > 0) {
    section("MAHALLU BREAKDOWN");
    R.push([
      "Mahallu Name",
      "Sponsorships",
      "Promised (₹)",
      "Collected (₹)",
      "Collection %",
    ]);
    s.mahalluBreakdown.forEach((m) => {
      R.push([m.name, m.count, fmt(m.promisedINR), fmt(m.paidINR), m.rate]);
    });
  }

  // ── Activity
  section("ACTIVITY");
  kv("Total Payments Recorded", s.activity.totalPayments);

  const ws = XLSX.utils.aoa_to_sheet(R);
  setColWidths(ws, [40, 22, 20, 20, 16]);
  return ws;
}

/**
 * Main export function.
 * @param {Array} allStats - array of chapter stats from the API
 */
export function exportAllChapterStats(allStats) {
  if (!allStats || allStats.length === 0) return;

  const wb = XLSX.utils.book_new();

  // 1. Summary sheet (first)
  XLSX.utils.book_append_sheet(wb, buildSummarySheet(allStats), "Summary");

  // 2. One detailed sheet per chapter
  const usedNames = new Set(["Summary"]);
  allStats.forEach((s) => {
    let sheetName = s.chapter.name.slice(0, 31).replace(/[\\/*?[\]:]/g, "_");
    // Deduplicate if two chapters share truncated names
    let candidate = sheetName;
    let suffix = 2;
    while (usedNames.has(candidate)) {
      candidate = `${sheetName.slice(0, 28)}_${suffix++}`;
    }
    usedNames.add(candidate);
    XLSX.utils.book_append_sheet(wb, buildChapterSheet(s), candidate);
  });

  const today = new Date().toISOString().split("T")[0];
  XLSX.writeFile(wb, `Taawun-Chapter-Stats-${today}.xlsx`);
}
